import React from 'react';
import { Body, Headline } from '@sato/components';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { BODY_MAX_LENGTH, TOPIC_MAX_LENGTH } from '@constants';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';
import { CommonSpacesDropdown } from '@views/MessagesPage/MessageForms/components/CommonSpacesDropdown';

import {
  IncidentFormWrapper,
  IncidentReportFields,
} from '../../IncidentFormWrapper';

type BuildingDefectFormValues = IncidentReportFields & { location: string };

type CommonAreaDefectParameters = {
  locationSelectionItems: Record<string, string>;
};

const isValidParameters = (
  value: unknown
): value is CommonAreaDefectParameters =>
  typeof value === 'object' && !!value && 'locationSelectionItems' in value;

export const BuildingDefectCommonAreas = ({
  parameters,
  customTranslations,
}: ParsedEntry<IWidget>) => {
  const { t, tContentful } = useCustomTranslations(customTranslations);

  const { create } = useIncidentCreation({
    postIncident: api.incidents.postGenericDefectIncident,
    classifications: {
      classification: IncidentClassification.DEFECT_REPORT_PUBLIC,
      subClassification: IncidentSubClassification.PUBLIC_PLACES,
    },
  });

  if (!isValidParameters(parameters)) return null;

  const transformCommonAreas = ({
    location,
    description,
    ...values
  }: BuildingDefectFormValues) => ({
    ...values,
    description: [`${t('locationLabel')}: ${location}`, description].join(
      '\n\n'
    ),
  });

  return (
    <IncidentFormWrapper<BuildingDefectFormValues>
      incidentKind="BuildingDefectCommonAreas"
      defaultValues={{
        title: '',
        description: '',
        files: [],
        location: '',
      }}
      validationSchema={yup.object({
        title: yup.string().required(),
        description: yup.string().required(),
        location: yup.string().required(),
        files: yup.array().files(tContentful),
      })}
      onSubmit={(values) => create(transformCommonAreas(values))}
      withFileUpload
    >
      <div className="flex flex-col gap-3">
        <Headline level={3}>
          {tContentful('defectFormDescriptionTitle')}
        </Headline>

        <Body>{tContentful('defectFormDescriptionBody')}</Body>
      </div>

      <CommonSpacesDropdown name="location" label={t('locationLabel')} />

      <HookFormTextInput
        type="text"
        name="title"
        label={{ value: t('titleLabel'), type: 'static' }}
        placeholder={t('titlePlaceholder')}
        maxLength={TOPIC_MAX_LENGTH}
      />

      <HookFormTextArea
        type="text"
        label={{ value: t('descriptionLabel'), type: 'static' }}
        name="description"
        placeholder={t('descriptionPlaceholder')}
        message={t('descriptionHelper')}
        rows={6}
        maxLength={BODY_MAX_LENGTH}
      />
    </IncidentFormWrapper>
  );
};
